import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { getDataCompletenessAnalysis } from '../firebase/functionsService';
import '../styles/DataCompleteness.css';

function DataCompleteness() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' or 'incomplete'

  useEffect(() => {
    loadAnalysis();
  }, []);

  const loadAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDataCompletenessAnalysis('1269');
      setAnalysis(result);
    } catch (err) {
      console.error('Error loading data completeness:', err);
      setError(err.message || 'Failed to load data completeness analysis');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!analysis || !analysis.incompleteProfiles) return;

    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Completeness %', 'Missing Fields', 'Critical Missing', 'Emergency Contact Missing'];
    const rows = analysis.incompleteProfiles.map(profile => [
      profile.firstName,
      profile.lastName,
      profile.email,
      profile.phone,
      profile.completeness,
      profile.missingFields.join('; '),
      profile.criticalMissing ? 'Yes' : 'No',
      profile.emergencyContactMissing ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incomplete-profiles-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getFieldChartData = () => {
    if (!analysis || !analysis.fieldStats) return [];

    return Object.entries(analysis.fieldStats).map(([key, stats]) => ({
      name: stats.label,
      percentage: stats.percentage,
      count: stats.count,
      category: stats.category
    }));
  };

  const getCategoryColor = (category) => {
    const colors = {
      critical: '#ef4444',
      contact: '#f59e0b',
      address: '#3b82f6',
      emergency: '#10b981'
    };
    return colors[category] || '#6b7280';
  };

  const getCompletenessColor = (percentage) => {
    if (percentage >= 90) return '#10b981'; // Green
    if (percentage >= 70) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading data completeness analysis...</p></div>
    );
  }

  if (error) {
    return (
      <div className="data-completeness-container">
        <div className="error-message">
          <h3>Error Loading Analysis</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadAnalysis}>Retry</button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="data-completeness-container">
        <div className="error-message">No analysis data available</div>
      </div>
    );
  }

  const chartData = getFieldChartData();

  return (
    <div className="data-completeness-container">
      {/* Header */}
      <div className="data-completeness-header">
        <button className="btn-back" onClick={() => navigate('/admin-panel')}>
          ← Back to Admin Panel
        </button>
        <h1>Data Completeness Analysis</h1>
        <button className="btn-refresh" onClick={loadAnalysis}>
          Refresh
        </button>
      </div>

      {/* View Mode Toggle */}
      <div className="view-toggle">
        <button
          className={`toggle-btn ${viewMode === 'overview' ? 'active' : ''}`}
          onClick={() => setViewMode('overview')}
        >
          Overview
        </button>
        <button
          className={`toggle-btn ${viewMode === 'incomplete' ? 'active' : ''}`}
          onClick={() => setViewMode('incomplete')}
        >
          Incomplete Profiles ({analysis.incompleteCount})
        </button>
      </div>

      {viewMode === 'overview' ? (
        <>
          {/* Summary Cards */}
          <div className="summary-grid">
            <div className="summary-card">
              <div className="card-icon">👥</div>
              <div className="card-content">
                <h3>Total Guests</h3>
                <p className="card-value">{analysis.totalParticipants}</p>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <h3>Complete Profiles</h3>
                <p className="card-value">
                  {analysis.completeProfiles} ({analysis.completeProfilesPercentage}%)
                </p>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon">⚠️</div>
              <div className="card-content">
                <h3>Incomplete Profiles</h3>
                <p className="card-value">
                  {analysis.incompleteCount} ({100 - analysis.completeProfilesPercentage}%)
                </p>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-icon">📊</div>
              <div className="card-content">
                <h3>Overall Completeness</h3>
                <p
                  className="card-value"
                  style={{ color: getCompletenessColor(analysis.overallCompleteness) }}
                >
                  {analysis.overallCompleteness}%
                </p>
              </div>
            </div>
          </div>

          {/* Category Statistics */}
          <div className="category-section">
            <h2>Completeness by Category</h2>
            <div className="category-grid">
              <div className="category-card" style={{ borderLeftColor: getCategoryColor('critical') }}>
                <h3>Critical Information</h3>
                <div className="category-percentage">
                  {analysis.categoryStats?.critical?.percentage || 0}%
                </div>
                <p className="category-desc">Name, Gender, Birthday</p>
              </div>

              <div className="category-card" style={{ borderLeftColor: getCategoryColor('contact') }}>
                <h3>Contact Information</h3>
                <div className="category-percentage">
                  {analysis.categoryStats?.contact?.percentage || 0}%
                </div>
                <p className="category-desc">Phone, Email</p>
              </div>

              <div className="category-card" style={{ borderLeftColor: getCategoryColor('address') }}>
                <h3>Address Information</h3>
                <div className="category-percentage">
                  {analysis.categoryStats?.address?.percentage || 0}%
                </div>
                <p className="category-desc">Street, City, State, Zip</p>
              </div>

              <div className="category-card" style={{ borderLeftColor: getCategoryColor('emergency') }}>
                <h3>Emergency Contact</h3>
                <div className="category-percentage">
                  {analysis.categoryStats?.emergency?.percentage || 0}%
                </div>
                <p className="category-desc">Name, Phone</p>
              </div>
            </div>
          </div>

          {/* Field Completeness Chart */}
          <div className="chart-section">
            <h2>Field-by-Field Completeness</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis
                  label={{ value: 'Completion %', angle: -90, position: 'insideLeft' }}
                  domain={[0, 100]}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="custom-tooltip">
                          <p className="tooltip-label">{data.name}</p>
                          <p className="tooltip-value">
                            {data.percentage}% ({data.count}/{analysis.totalParticipants})
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="percentage" name="Completion %" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getCategoryColor(entry.category)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Field Details Table */}
          <div className="table-section">
            <h2>Detailed Field Statistics</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Category</th>
                  <th>Completed</th>
                  <th>Missing</th>
                  <th>Percentage</th>
                  <th>Visual</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((field, index) => {
                  const missing = analysis.totalParticipants - field.count;
                  return (
                    <tr key={index}>
                      <td className="field-name">{field.name}</td>
                      <td>
                        <span
                          className="category-badge"
                          style={{ backgroundColor: getCategoryColor(field.category) }}
                        >
                          {field.category}
                        </span>
                      </td>
                      <td>{field.count}</td>
                      <td>{missing}</td>
                      <td>
                        <span style={{ color: getCompletenessColor(field.percentage) }}>
                          {field.percentage}%
                        </span>
                      </td>
                      <td>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{
                              width: `${field.percentage}%`,
                              backgroundColor: getCategoryColor(field.category)
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        /* Incomplete Profiles View */
        <div className="incomplete-section">
          <div className="section-header">
            <h2>Incomplete Profiles ({analysis.incompleteCount})</h2>
            <button className="btn btn-primary" onClick={exportToCSV}>
              Export to CSV
            </button>
          </div>

          {analysis.incompleteProfiles.length === 0 ? (
            <div className="empty-state">
              <p>All profiles are complete!</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Completeness</th>
                  <th>Missing Fields</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {analysis.incompleteProfiles.map((profile, index) => (
                  <tr key={index}>
                    <td className="profile-name">
                      {profile.firstName} {profile.lastName}
                    </td>
                    <td>
                      <div className="contact-info">
                        <div>{profile.email || 'No email'}</div>
                        <div>{profile.phone || 'No phone'}</div>
                      </div>
                    </td>
                    <td>
                      <div className="completeness-indicator">
                        <span style={{ color: getCompletenessColor(profile.completeness) }}>
                          {profile.completeness}%
                        </span>
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{
                              width: `${profile.completeness}%`,
                              backgroundColor: getCompletenessColor(profile.completeness)
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="missing-fields">
                      {profile.missingFields.length > 0 ? (
                        <div className="missing-list">
                          {profile.missingFields.slice(0, 3).map((field, i) => (
                            <span key={i} className="missing-badge">{field}</span>
                          ))}
                          {profile.missingFields.length > 3 && (
                            <span className="missing-badge">+{profile.missingFields.length - 3} more</span>
                          )}
                        </div>
                      ) : (
                        'None'
                      )}
                    </td>
                    <td>
                      <div className="status-badges">
                        {profile.criticalMissing && (
                          <span className="status-badge critical">Critical Missing</span>
                        )}
                        {profile.emergencyContactMissing && (
                          <span className="status-badge emergency">No Emergency Contact</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default DataCompleteness;
